import { HTMLProps, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Toggle from 'react-toggle';
import { IconElements } from '../../../../config/icons';
import { ModalScreenPension } from '../../../general/ModalScreen';
import { OverlayHeader } from '../../OverlayHeader';
import { validateEmail, validateName, validatePhone } from '../../../../utils/validators';
import { PensionProgressSaverContext } from './PensionProgressSaverContext';
import { Button } from '../../../general/Button';
import {
    EMAIL_HOOK_PENSION_ROBOT_URL,
    PRIVACY_STATEMENT_PENSION,
    ROBOT_ENVIRONMENT,
} from '../../../../config/constants';
import { useMutation } from 'react-query';
import { RobotEnvironmentContext } from '../../../../contexts/general/RobotEnvironmentContext';
import { getPensionToolDirectLinkWithToken } from '../../../../utils/functions';
import { Link } from '../../../general/Link';

export const SaveMarketingModal: React.FC<{
    isShown: boolean;
    setIsShown: (val: boolean) => void;
    onSaveData: () => void;
}> = ({ isShown, setIsShown, onSaveData }) => {
    const { t } = useTranslation('pension');
    const { pensionProgressSaverData, setPensionProgressSaverData } = useContext(PensionProgressSaverContext);
    const { environment, userToken } = useContext(RobotEnvironmentContext);

    const onClose = () => setIsShown(false);

    const getAllInputValid = () =>
        validateEmail(pensionProgressSaverData.contactDetails.email) &&
        validateName(pensionProgressSaverData.contactDetails.name) &&
        (pensionProgressSaverData.contactDetails.phone === '' ||
            validatePhone(pensionProgressSaverData.contactDetails.phone)) &&
        pensionProgressSaverData.mayUseData;

    type ContactDetails = {
        name: string;
        email: string;
        phone: string;
        advisorEmail: string;
        validInput: boolean;
    };

    const sendEmail = async function () {
        await sendEmailMutation.mutate({
            environment: environment,
            mayCall: pensionProgressSaverData.mayCallChecked,
            contactDetails: {
                name: pensionProgressSaverData.contactDetails.name,
                email: pensionProgressSaverData.contactDetails.email,
                phone: pensionProgressSaverData.contactDetails.phone,
                advisorEmail: '',
                validInput: getAllInputValid(),
            },
            link: getPensionToolDirectLinkWithToken(userToken),
            metadata: {
                date: Date.now(),
                source: window.location.toString(),
                tool: 'pension',
            },
        });
    };

    const sendEmailMutation = useMutation<
        boolean,
        unknown,
        {
            environment: ROBOT_ENVIRONMENT;
            contactDetails: ContactDetails;
            mayCall: boolean;
            link: string;
            metadata: { date: number; tool: 'investment' | 'funds' | 'pension'; source: string };
        }
    >(async (call) => {
        const validation = getAllInputValid();

        if (!validation) throw Error('Input not valid');

        const response = await fetch(EMAIL_HOOK_PENSION_ROBOT_URL, {
            method: 'POST',
            body: JSON.stringify(call),
        });

        if (!response.ok) throw Error('Request not succesful');
        onClose && onClose();
        return response.ok;
    });

    return (
        <ModalScreenPension isShown={isShown} useStdModalStyle={false} className="mx-auto md:max-w-screen-sm">
            <div className="bg-white md:mx-auto md:rounded-xl p-8">
                <OverlayHeader title={t('pension-save-marketing-modal-title')} onClose={onClose} />
                <div className="flex flex-col gap-6">
                    <div>
                        <span>{t('pension-save-marketing-modal-text')}</span>
                    </div>
                    <SaveModalInputFields
                        inputFields={pensionProgressSaverData.contactDetails}
                        setInputFields={({ name, email, phone }) =>
                            setPensionProgressSaverData &&
                            setPensionProgressSaverData({
                                ...pensionProgressSaverData,
                                contactDetails: {
                                    name,
                                    email,
                                    phone,
                                },
                            })
                        }
                    />
                    <SaveModalToggles
                        conditionSaving={pensionProgressSaverData.mayUseData}
                        setConditionSaving={(savingCheck: boolean) =>
                            setPensionProgressSaverData &&
                            setPensionProgressSaverData({
                                ...pensionProgressSaverData,
                                mayUseData: savingCheck,
                            })
                        }
                        conditionContact={pensionProgressSaverData.mayCallChecked}
                        setConditionContact={(contactCheck: boolean) =>
                            setPensionProgressSaverData &&
                            setPensionProgressSaverData({
                                ...pensionProgressSaverData,
                                mayCallChecked: contactCheck,
                            })
                        }
                    />
                    <div>
                        <span>
                            {t('pension-legal-page-toggle-disclaimer-prefix-text')}
                            <Link
                                className="text-bnd-green underline"
                                href={PRIVACY_STATEMENT_PENSION}
                                target="_blank"
                                rel="noopener noreferrer"
                                linkColor="green"
                            >
                                {t('pension-legal-page-toggle-disclaimer-link-text')}
                            </Link>
                            {t('pension-legal-page-toggle-disclaimer-suffix-text')}
                        </span>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Button
                            className={`w-full px-4 ${!getAllInputValid() && 'opacity-50'}`}
                            buttonType="primary"
                            isDisabled={!getAllInputValid()}
                            onClick={() => {
                                onSaveData();
                                getAllInputValid() && sendEmail();
                            }}
                        >
                            {t('general-send')}
                        </Button>
                        <Button className={`w-full px-4`} buttonType="secondary" onClick={onClose}>
                            {t('general-cancel')}
                        </Button>
                    </div>
                </div>
            </div>
        </ModalScreenPension>
    );
};

const SaveModalToggles: React.FC<{
    conditionSaving: boolean;
    setConditionSaving: (val: boolean) => void;
    conditionContact: boolean;
    setConditionContact: (val: boolean) => void;
}> = ({ conditionSaving, setConditionSaving, conditionContact, setConditionContact }) => {
    const { t } = useTranslation('pension');

    return (
        <>
            <div className="flex flex-row gap-4 items-center">
                <Toggle
                    className="mr-4"
                    icons={false}
                    checked={conditionSaving}
                    onChange={(e) => setConditionSaving(e.target.checked)}
                />
                <span>{t('pension-save-marketing-modal-condition-saving')}</span>
            </div>
            <div className="flex flex-row gap-4 items-center">
                <Toggle
                    className="mr-4"
                    icons={false}
                    checked={conditionContact}
                    onChange={(e) => setConditionContact(e.target.checked)}
                />
                <span>{t('pension-save-marketing-modal-condition-contact')}</span>
            </div>
        </>
    );
};

const SaveModalInputFields: React.FC<{
    inputFields: { name: string; email: string; phone: string };
    setInputFields: ({ name, email, phone }: { name: string; email: string; phone: string }) => void;
}> = ({ inputFields, setInputFields }) => {
    const { t } = useTranslation('pension');

    const setName = (name: string) => setInputFields({ ...inputFields, name: name });

    const setEmail = (email: string) => setInputFields({ ...inputFields, email: email });

    const setPhone = (phone: string) => setInputFields({ ...inputFields, phone: phone });

    return (
        <div className="flex flex-col gap-2">
            <SaveModalInputField
                placeholder={t('pension-save-marketing-modal-name-placeholder')}
                isValid={validateName(inputFields.name)}
                value={inputFields.name}
                onChange={setName}
            />
            <SaveModalInputField
                placeholder={t('pension-save-marketing-modal-email-placeholder')}
                isValid={validateEmail(inputFields.email)}
                value={inputFields.email}
                onChange={setEmail}
            />
            <SaveModalInputField
                placeholder={t('pension-save-marketing-modal-phone-placeholder')}
                isValid={validatePhone(inputFields.phone)}
                value={inputFields.phone}
                onChange={setPhone}
            />
        </div>
    );
};

const SaveModalInputField: React.FC<
    {
        placeholder?: string;
        isValid: boolean;
        value: string;
        onChange: (input: string) => void;
    } & Omit<HTMLProps<HTMLDivElement>, 'value' | 'onChange'>
> = ({ placeholder, isValid, value, onChange, className, ...rest }) => {
    return (
        <div
            className={`relative flex flex-row items-center bg-white border border-gray-200 rounded-lg pr-4 ${
                isValid ? 'border border-bnd-green' : ''
            } ${className}`}
            {...rest}
        >
            <input
                className={`flex-1 p-2 focus:outline-hidden rounded-lg font-medium`}
                value={value}
                type="Text"
                placeholder={placeholder}
                onChange={(event) => {
                    onChange(event.target.value);
                }}
            ></input>
            <div className="absolute top-0 bottom-0 right-2 flex items-center justify-center">
                <IconElements.Check
                    className={`transition-opacity ${isValid ? '' : 'opacity-0'} text-bnd-green w-3 h-4`}
                />
            </div>
        </div>
    );
};
